/**
 * Coerce a money value of unknown type to a Number that is SAFE TO ADD. Returns 0 for anything
 * unusable, so a bad row contributes nothing instead of poisoning a running total.
 *
 * Why this exists: the API returns currency fields sourced straight from nvarchar(max) columns, so a
 * value may arrive as a string ("2500.00", "$18,500.00"). Summing with `sum + (r.premium || 0)` then
 * flips `+` from addition to CONCATENATION the moment one string enters — "2500.00" + 1340000 is
 * "2500.001340000". formatPrice() parseFloats that digit-soup back into a plausible-looking figure,
 * which is how the Not Ready tab reported $2,500.01 for 41 policies including two seven-figure
 * premiums: the first row's 2500.00 with every later row's digits spilling into the decimals.
 *
 * The API is being fixed to send Numbers as well; this stays because the UI must not depend on that.
 */
export const toMoneyNumber = (value: unknown): number => {
    if (value === null || value === undefined) return 0;
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    const raw = String(value).trim();
    if (raw === "") return 0;
    const negatedByParens = /^\(.*\)$/.test(raw);
    const cleaned = raw.replace(/[()$,\s]/g, "");
    if (cleaned === "" || !/^-?\d*\.?\d+$/.test(cleaned)) return 0;
    // "(-100)" is doubly negative and therefore ambiguous — reject rather than guess, matching the
    // API's moneyNumber(). Without this the two implementations disagree (0 vs +100) and they must not.
    if (negatedByParens && cleaned.startsWith("-")) return 0;
    const n = Number(cleaned);
    if (!Number.isFinite(n)) return 0;
    return negatedByParens ? -n : n;
};

const formatPrice = (amount: string | number | null | undefined): string => {
    try {
        if (amount === null || amount === undefined) {
            return "";
        }

        const amountString = String(amount).trim();
        if (
            amountString === "" ||
            amountString === "$-" ||
            amountString.toLowerCase() === "null" ||
            amountString.toLowerCase() === "$null" ||
            amountString.toLowerCase() === "undefined"
        ) {
            return "";
        }

        const normalizedAmount = amountString.replace("$", "").replace(/,/g, "");
        const price = parseFloat(normalizedAmount);
        if (isNaN(price)) {
            return "";
        }

        return price.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 2,
        });
    } catch (err) {
        return "";
    }
}

export const CorrectedformatCurrency = (value: string): string => {
    if (!value) {
      return ""; // Return empty string if the input is empty
    }
  
    const numValue = parseFloat(value); // Convert string to number
  
    if (isNaN(numValue)) {
      return ""; // Return empty string if the conversion results in NaN
    }
  
    if (numValue < 0) {
      return `(${Math.abs(numValue).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      })})`;
    } else {
      return numValue.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      });
    }
  }

export default formatPrice;